import { useState, useEffect, SyntheticEvent, useMemo } from "react";
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import axios from "axios";
import { ConfirmDialog, DateField, StatCard, type DataTableColumn } from '@routes-plan/ui';
import { Office } from "../../models/office";
import { Route } from "../../models/route";
import { Loan } from "../../models/loan";
import { Payment } from "../../models/payment";
import PaymentHistoryModal from './modals/PaymentHistoryModal';
import EditPaymentByDateModal from './modals/EditPaymentByDateModal';
import { useSelection } from '../../components/context/SelectionContext';
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString, operationalTimezone } from '../../utils/operationalDateTime';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import {
  isOperationalDateEligibleForPayDelete,
  isWebSuperAdminPrincipal,
} from '../../utils/payDeleteEligibility';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { companyLocale } from '../../utils/format/currency';
import {
  OfficeSelect,
  ReportEmpty,
  ReportFilters,
  ReportPage,
  ReportResults,
  ReportStats,
  ReportTable,
  RouteMultiSelect,
  RowAction,
} from './components/ReportLayout';

interface PaymentRow {
  id: number | string;
  paymentId: number | string;
  route: string;
  clientIdNumber: string;
  fullName: string;
  discount: number;
  value: number;
  date: string;
  time: string;
  invoice: string;
  bankReceipt: string;
  balance: number | string;
  user: string;
  routeId: number | string;
}

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const PaymentsByDate: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  setPageTitle('menu.reports.payments-by-date');
  const t = useT();
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [marcado, setMarcado] = useState("");
  const [payments, setPayments] = useState<Payment[]>([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [openHistorico, setOpenHistorico] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [payId, setPayId] = useState<string | null>(null);
  const [payValue, setPayValue] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [routeId, setRouteId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [payDate, setPayDate] = useState<string | null>(null);
  const { office_id, setOffice_id } = useSelection();
  const [queried, setQueried] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: number | string; date: string } | null>(null);
  const { canRead, canUpdate, canDelete, authUserData } =
    useResourcePermissions('/reports/payments-by-date');
  const isSuperAdmin = isWebSuperAdminPrincipal(authUserData);

  const getLocalDate = () => nowOperationalDateString();
  const currentOperationalDate = getLocalDate();

  const [start_date, setStart_date] = useState(getLocalDate());
  const [end_date, setEnd_date] = useState(getLocalDate());

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        showLoading();
        const { data } = await axios.get("offices");
        setOffices(data.data);
      } catch (error) {
        console.error('Error fetching offices:', error);
      } finally {
        hideLoading();
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchRoutes = async () => {
      if (office_id) {
        try {
          const { data } = await axios.get(`offices/${office_id}/routes`);
          setOfficeRoutes(data);
          setSelectedRouteIds([]);
          setMarcado("");
          setLoans([]);
          setPayments([]);
          setSelectAll(false);
        } catch (error) {
          console.error("Error fetching routes:", error);
        }
      }
    };

    fetchRoutes();
  }, [office_id]);

  useEffect(() => {
    const fetchLoans = async () => {
      if (marcado) {
        try {
          const { data } = await axios.get(`routes/${marcado}/loans`);
          setLoans(data.data);
        } catch (error) {
          console.error("Error fetching loans:", error);
        }
      }
    };

    fetchLoans();
  }, [marcado]);
  const fetchPayments = async () => {
    try {
      showLoading();
      setPayments([]);
      const response = await axios.post("/reports/payments-by-date", {
        endDate: end_date,
        startDate: start_date,
        routeId: selectedRouteIds,
      });
      setPayments(response.data.data);
      if (response.data.data.length === 0) {
        showAlert(t('paymentReports.byDate.noRecordsFound'), 'info');
      } else {
        showAlert(t('paymentReports.byDate.querySuccess'), 'success');
      }
    } catch (error) {
      console.error("paybydate:", error);
      const msg =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof (error.response.data as { message?: string }).message === "string"
          ? (error.response.data as { message: string }).message
          : null;
      showAlert(msg || t('paymentReports.byDate.queryError'), "error");
    } finally {
      hideLoading();
    }
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!office_id || !start_date || !end_date || selectedRouteIds.length === 0) {
      showAlert(t('paymentReports.byDate.pendingFields'), 'warning');
      return;
    }

    const formData = {
      endDate: end_date,
      startDate: start_date,
      routeId: selectedRouteIds,
    };

    const url = "/reports/payments-by-date";
    try {
      showLoading();
      setQueried(true);
      setPayments([]);
      const response = await axios.post(url, formData);
      const data = response.data.data;
      setPayments(data);
      if (data.length === 0) {
        showAlert(t('paymentReports.byDate.noRecordsFound'), 'info');
      } else {
        showAlert(t('paymentReports.byDate.querySuccess'), 'success');
      }
    } catch (error) {
      console.error("paybydate:", error);
      const msg =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof (error.response.data as { message?: string }).message === "string"
          ? (error.response.data as { message: string }).message
          : null;
      showAlert(msg || t('paymentReports.byDate.queryError'), "error");
    } finally {
      hideLoading();
    }
  };

  const handleDeletePay = async (
    payIdValue: number | string | null | undefined,
    payDateValue?: string | null,
    confirmed = false,
  ) => {
    const id = Number(payIdValue);
    if (Number.isNaN(id)) {
      showAlert(t('paymentReports.byDate.invalidPaymentId'), 'warning');
      return;
    }
    if (
      payDateValue &&
      !isOperationalDateEligibleForPayDelete({
        payDateYmd: payDateValue,
        todayYmd: currentOperationalDate,
        superAdmin: isSuperAdmin,
      })
    ) {
      showAlert(
        isSuperAdmin
          ? t('paymentReports.byDate.deleteLimitAdmin')
          : t('paymentReports.byDate.deleteLimitToday'),
        'warning',
      );
      return;
    }
    if (!confirmed) {
      setPendingDelete({ id, date: payDateValue ?? '' });
      return;
    }

    try {
      showLoading();
      const { data } = await axios.post('/payments/bulk-delete', { id });
      showAlert(
        data?.code === 'CORRECTION_PENDING' ? t('paymentCorrections.deletePendingApproval') : t('paymentReports.byDate.deletedOk'),
        data?.code === 'CORRECTION_PENDING' ? 'info' : 'success',
      );
      await fetchPayments();
    } catch (error) {
      console.error('Error eliminando abono:', error);
      const msg =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof (error.response.data as { message?: string }).message === 'string'
          ? (error.response.data as { message: string }).message
          : null;
      showAlert(msg || t('paymentReports.byDate.deleteError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const decimalFormatter = useMemo(() => new Intl.NumberFormat(companyLocale(), { style: 'decimal' }), []);

  const convertirFechaHoraBogota = (isoDate: string) => {
    const localDate = moment.utc(isoDate).tz(operationalTimezone());
    return {
      fecha: localDate.format('YYYY-MM-DD'),
      hora: localDate.format('HH:mm:ss'),
    };
  };

  const dataRows: PaymentRow[] = [...payments]
    .sort((a, b) => {
      const routeCompare = String(a.loan.route.name || '').localeCompare(
        String(b.loan.route.name || ''),
        'es',
        { sensitivity: 'base' }
      );
      if (routeCompare !== 0) return routeCompare;
      return moment(b.operationDate).valueOf() - moment(a.operationDate).valueOf();
    })
    .map((payment: Payment) => {
      const { fecha, hora } = convertirFechaHoraBogota(payment.operationDate);
      return {
        id: payment.id,
        paymentId: payment.id,
        route: payment.loan.route.name,
        clientIdNumber: payment.loan.client.idNumber,
        fullName: payment.loan.client.fullName,
        discount: Number(payment.discountValue ?? 0),
        value: Number(payment.value ?? 0),
        date: fecha,
        time: hora,
        invoice: payment.invoice || '',
        bankReceipt: payment.bankReceipt || '',
        balance: payment.balance,
        user: payment.user.username,
        routeId: payment.loan.route.id,
      };
    });

  const totalValue = dataRows.reduce((sum, row) => Number(sum + (Number(row.value) || 0)), 0);
  const totalDiscount = dataRows.reduce((sum, row) => Number(sum + (Number(row.discount) || 0)), 0);

  const columns: DataTableColumn<PaymentRow>[] = [
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.route, sortable: true },
    { id: 'clientIdNumber', header: t('common.idNumber'), value: (r) => r.clientIdNumber, sortable: true, hideBelow: 'sm' },
    { id: 'fullName', header: t('paymentReports.byDate.colClient'), value: (r) => r.fullName, sortable: true },
    { id: 'value', header: t('paymentReports.byDate.colValue'), value: (r) => r.value, render: (r) => decimalFormatter.format(r.value), align: 'right', sortable: true },
    { id: 'discount', header: t('paymentReports.byDate.colDiscount'), value: (r) => r.discount, render: (r) => decimalFormatter.format(r.discount), align: 'right', sortable: true },
    { id: 'date', header: t('common.date'), value: (r) => r.date, sortable: true },
    { id: 'time', header: t('paymentReports.byDate.colTime'), value: (r) => r.time, sortable: true },
    { id: 'invoice', header: t('paymentReports.byDate.colReceipt'), value: (r) => r.invoice, hideBelow: 'md' },
    { id: 'bankReceipt', header: t('paymentReports.byDate.colBankReceipt'), value: (r) => r.bankReceipt, hideBelow: 'md' },
    { id: 'user', header: t('common.user'), value: (r) => r.user, sortable: true },
  ];

  const handleCloseHistorico = () => {
    setClientId(null);
    setRouteId(null);
    setOpenHistorico(false);
  };
  const handleCloseEdit = async () => {
    setPayId(null);
    setPayValue(null);
    setBalance(null);
    setFullName(null);
    setClientId(null);
    setPayDate(null);
    setOpenEditar(false);

    // Volver a cargar los pagos después de editar
    await fetchPayments();
  };

  return (
    <ReportPage title={t('paymentReports.byDate.title')} description={t('paymentReports.byDate.description')}>
      <ReportFilters onSubmit={() => submit({ preventDefault: () => undefined } as SyntheticEvent)}>
        <DateField
          label={t('paymentReports.byDate.startDate')}
          value={start_date}
          onChange={(v) => setStart_date(v ?? '')}
          max={minYmd(end_date, nowOperationalDateString())}
          size="small"
          fullWidth
          required
        />
        <DateField
          label={t('paymentReports.byDate.endDate')}
          value={end_date}
          onChange={(v) => setEnd_date(v ?? '')}
          min={start_date}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
          required
        />
        <OfficeSelect offices={offices} value={office_id} onChange={(id) => setOffice_id(id)} required />
        <RouteMultiSelect
          routes={officeRoutes}
          value={selectedRouteIds}
          onChange={(ids) => {
            setSelectedRouteIds(ids);
            setSelectAll(ids.length > 0 && ids.length === officeRoutes.length);
          }}
          required
        />
      </ReportFilters>

      {payments.length > 0 && (
        <ReportStats>
          <StatCard label={t('paymentReports.byDate.totalValue')} value={decimalFormatter.format(totalValue)} />
          <StatCard label={t('paymentReports.byDate.totalDiscount')} value={decimalFormatter.format(totalDiscount)} />
          <StatCard label={t('paymentReports.byDate.paymentCount')} value={decimalFormatter.format(dataRows.length)} />
        </ReportStats>
      )}

      <ReportResults>
        <ReportTable<PaymentRow>
          rows={dataRows}
          columns={columns}
          getRowId={(r) => r.id}
          exportFileName={t('paymentReports.byDate.exportFileName')}
          ariaLabel={t('paymentReports.byDate.title')}
          emptyState={<ReportEmpty queried={queried} />}
          rowActions={(row) => (
            <>
              <RowAction
                allowed={canRead}
                deniedTooltip={t('paymentReports.byDate.noPermissionHistory')}
                label={t('paymentReports.byDate.history')}
                icon={<TimelineOutlined fontSize="small" />}
                onClick={() => {
                  setOpenHistorico(true);
                  setClientId(String(row.clientIdNumber ?? ''));
                  setRouteId(row.routeId != null ? String(row.routeId) : null);
                }}
              />
              {row.date === currentOperationalDate && (
                <RowAction
                  allowed={canUpdate}
                  deniedTooltip={t('paymentReports.byDate.noPermissionEdit')}
                  label={t('common.edit')}
                  icon={<EditOutlined fontSize="small" />}
                  onClick={() => {
                    setOpenEditar(true);
                    setPayId(row.paymentId != null ? String(row.paymentId) : null);
                    setPayValue(Number(row.value ?? 0));
                    setBalance(Number(row.balance ?? 0));
                    setFullName(String(row.fullName ?? ''));
                    setClientId(String(row.clientIdNumber ?? ''));
                    setPayDate(String(row.date ?? ''));
                  }}
                />
              )}
              {isOperationalDateEligibleForPayDelete({
                payDateYmd: String(row.date ?? ''),
                todayYmd: currentOperationalDate,
                superAdmin: isSuperAdmin,
              }) && (
                <RowAction
                  allowed={canDelete}
                  deniedTooltip={t('paymentReports.byDate.noPermissionDelete')}
                  label={t('common.delete')}
                  color="error"
                  icon={<DeleteOutlined fontSize="small" />}
                  onClick={() => setPendingDelete({ id: row.paymentId, date: String(row.date ?? '') })}
                />
              )}
            </>
          )}
        />
      </ReportResults>

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('paymentReports.byDate.confirmDeleteTitle')}
        message={t('paymentReports.byDate.confirmDelete')}
        confirmLabel={t('common.delete')}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target) void handleDeletePay(target.id, target.date, true);
        }}
      />
      <PaymentHistoryModal
        open={openHistorico}
        onClose={handleCloseHistorico}
        clientId={clientId}
        routeId={routeId}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <EditPaymentByDateModal
        open={openEditar}
        onClose={handleCloseEdit}
        clientId={Number(clientId)}
        payId={payId}
        payment={payValue}
        payDate={payDate}
        balance={balance}
        fullName={fullName}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </ReportPage>
  );
};

export default PaymentsByDate;
